import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthShell } from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { issueNativeUpdateAuthorization, parseNativeUpdateAuthorizationSearch, validateNativeUpdateCallback } from "@/api/local-gateway-update";

const targetNames: Record<string, string> = {
  "darwin-arm64": "Mac ARM64", "windows-amd64": "Windows x64", "windows-arm64": "Windows ARM64",
};

export function NativeUpdateAuthorizationPage() {
  const location = useLocation();
  const request = useMemo(() => parseNativeUpdateAuthorizationSearch(location.search), [location.search]);
  const latestRequest = useRef(request);
  latestRequest.current = request;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const alive = useRef(true);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; controller.current?.abort(); };
  }, [location.search]);

  async function authorize() {
    if (!request || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    controller.current = new AbortController();
    try {
      const result = await issueNativeUpdateAuthorization(request, controller.current.signal);
      if (alive.current && latestRequest.current === request) window.location.assign(validateNativeUpdateCallback(request, result.callbackUri));
    } catch {
      if (alive.current && latestRequest.current === request) setError("更新の認証を完了できませんでした。Drive Gatewayからもう一度アップデートを実行してください。");
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <AuthShell title="C-RISE Drive Gatewayのアップデート" description="ログイン済みのアカウントで更新ファイルの取得を許可します。">
    {!request ? <p role="alert">更新要求を確認できません。Drive Gatewayからもう一度アップデートを実行してください。</p>
      : <div className="space-y-4">
        <p>{request.environment === "staging" ? "検証環境" : "開発環境"}・{targetNames[request.target]}</p>
        <p className="text-sm text-muted-foreground">続けると、このPCのDrive Gatewayへ戻ります。更新の進捗と結果はDrive Gatewayで確認できます。</p>
        {error && <p role="alert">{error}</p>}
        <Button onClick={() => { void authorize(); }} disabled={busy}>{busy ? "認証しています…" : "アップデートを続ける"}</Button>
      </div>}
  </AuthShell>;
}
